import { createAsyncThunk } from '@reduxjs/toolkit';

import { client } from 'src/graphql/client';
import { MARK_ROUTE_FOR_PROVIDER } from 'src/graphql/requests/markRouteForProvider';
import {
  GMarkRouteForProviderMutation,
  GThirdPartyProvider,
} from 'src/graphql/types/generated';
import { logWarning } from 'src/services/datadog';
import type { AppDispatch } from 'src/state/store';
import { firstUserErrorMessage } from 'src/utils';
import { NotificationType, showNotification } from '../notification';
import { refetchRouteList } from './refetchRouteList';

type MarkRouteForProviderArgs = {
  routeId: string;
  provider: GThirdPartyProvider;
  // ISO8601 in UTC; omitted means ASAP, decided by LDS at push time.
  pickupAt?: string;
  warehouseId: string;
  nightEnd: string;
};

// What the courier dialog needs from a provider mutation: a refusal carries
// LDS's message; no message means the response was lost.
export type ProviderRouteResult = { ok: boolean; message?: string };

export const markRouteForProvider = createAsyncThunk(
  'delivery/markRouteForProvider',
  async (
    {
      routeId,
      provider,
      pickupAt,
      warehouseId,
      nightEnd,
    }: MarkRouteForProviderArgs,
    thunkAPI,
  ): Promise<ProviderRouteResult> => {
    const refetch = (): void =>
      refetchRouteList(thunkAPI.dispatch as AppDispatch, {
        warehouseId,
        nightEnd,
      });

    const { data, error } = await client
      .mutation<GMarkRouteForProviderMutation>(MARK_ROUTE_FOR_PROVIDER, {
        input: { routeId, provider, ...(pickupAt ? { pickupAt } : {}) },
      })
      .toPromise();

    const payload = data?.markRouteForProvider;
    const userErrors = payload?.userErrors ?? [];

    if (userErrors.length > 0) {
      logWarning('third_party.mark_route_failed', {
        routeId,
        provider,
        userError: userErrors[0]?.__typename,
        transportError: false,
      });
      // "Already marked" usually means another GM got there first, and the
      // route list does not poll, so it is refreshed behind the dialog, which
      // stays open with the message.
      refetch();
      return { ok: false, message: firstUserErrorMessage(userErrors) };
    }

    if (error || !payload) {
      logWarning('third_party.mark_route_failed', {
        routeId,
        provider,
        transportError: Boolean(error),
      });
      thunkAPI.dispatch(showNotification({ type: NotificationType.Error }));
      // The mark may have committed before the response was lost; refetching
      // is what keeps a retry from meeting "already marked" against a stale
      // header.
      refetch();
      return { ok: false };
    }

    // The courier dialog stays open and shows the quotes arriving.
    refetch();
    return { ok: true };
  },
);
